import { BrowserRouter, useLocation } from "react-router-dom";
import { useEffect, Suspense } from "react";
import { trackPageView } from "./utils/analytics";
import RouteTitle from "./components/RouteTitle";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { LocationProvider } from "./context/LocationContext";
import { JourneyProvider } from "./context/JourneyContext";
import { ExpertModeProvider } from "./context/ExpertModeContext";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Premium UX enhancements (Audit Fix: TOP 1% UX)
import "./assets/premium-ux.css";

// ============================================================================
// LOADING FALLBACK COMPONENT (Premium Enhanced)
// ============================================================================
const PageLoader = () => (
  <div className="min-h-screen page-background flex items-center justify-center">
    <div className="text-center">
      <div className="loading-spinner-premium mx-auto mb-4" />
      <p className="text-[var(--text-muted)] loading-text-premium">Loading...</p>
    </div>
  </div>
);

// ============================================================================
// CRITICAL PATH — LOADED IMMEDIATELY (App shell + auth bootstrap)
// ============================================================================
import AuthCallback from "./pages/AuthCallback";
import Navigation from "./components/Navigation";
import QuickStartGuide from "./components/QuickStartGuide";
import CookieConsent from "./components/CookieConsent";
import OfflineIndicator from "./components/OfflineIndicator";
import "./App.css";

// All other routes live in routes-config.jsx
import { AppRoutesTree } from "./routes-config";

const AppRoutes = () => {
  const { user } = useAuth();
  const location = useLocation();

  // Track page views for analytics (PM Phase 16 — Content Performance)
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // CRITICAL: Detect session_id during render (not useEffect) to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <RouteTitle />
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-50 focus:bg-white focus:p-3 focus:text-sm"
        >
          Skip to main content
        </a>
        <main id="main-content" role="main">
          <AppRoutesTree user={user} />
        </main>
        {user && <Navigation />}
        {user && <QuickStartGuide />}
        <CookieConsent />
      </Suspense>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <JourneyProvider>
            <ExpertModeProvider>
              <LanguageProvider>
                <LocationProvider>
                  <AppRoutes />
                  <OfflineIndicator />
                  <Toaster position="top-center" richColors />
                </LocationProvider>
              </LanguageProvider>
            </ExpertModeProvider>
          </JourneyProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
